import { useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useCustomerServices } from "app/hooks/useCustomerServices";
import { useDashboardSlice } from "app/pages/Customer/MyAccount/Dashboard/slice";
import { useFixedPhoneDashboard } from "../../../../pages/Customer/FixedPhone/Account/Dashboard/hooks/useFixedPhoneDashboard";
import { useFixedPhoneSlice } from "app/pages/Customer/FixedPhone/slice";
import { useServiceTab } from "app/components/AdminLayout/MyAccountLayout/service-tab-context";
import { isDigitalSecurityAccountActiveStatus } from "app/pages/Customer/MyAccount/Dashboard/utils/digital-security-dashboard-helpers";

function clampFixedPhoneLineIndex(index: number, length: number): number {
  if (!length || length <= 0) {
    return 0;
  }
  return Math.min(Math.max(0, index), length - 1);
}

export type AccountStatusViewModel = {
  isActive: boolean | null;
  isLoading: boolean;
  getAccountStatus: () => void;
};

export const useAccountStatus = (): AccountStatusViewModel => {
  const {
    customerId,
    hasFixedPhoneService,
    hasCellularMobile,
    hasDigitalSecurityPlan,
  } = useCustomerServices();
  const location = useLocation();
  const {
    dashboardActiveTab,
    simServiceActiveTab,
    fixedPhoneDashboardSubTabIndex,
    fixedPhoneSimServiceSubTabIndex,
  } = useServiceTab();
  const {
    useLazyGetDashboardDataQuery,
    useLazyGetDigitalSecurityDashboardQuery,
  } = useDashboardSlice();
  const [
    getDashboardData,
    {
      data: mobileDashboardData,
      isLoading: isMobileDashboardLoading,
      // isFetching: isMobileDashboardFetching,
    },
  ] = useLazyGetDashboardDataQuery();

  const [
    getDigitalSecurityDashboard,
    { data: digitalSecurityDashboardData, isLoading: isDigitalSecurityLoading },
  ] = useLazyGetDigitalSecurityDashboardQuery();

  const {
    dashboardData: fixedPhoneDashboardData,
    isLoading: isFixedPhoneDashboardLoading,
    getFixedPhoneDashboard,
  } = useFixedPhoneDashboard(customerId);

  // Only fetch account details if we are on the Sim Service page AND the Fixed Phone tab is active
  const isOnSimService = location.pathname.includes("/my-account/sim-service");
  const shouldFetchAccountDetails =
    isOnSimService && simServiceActiveTab === "fixedPhone";

  const { useGetFixedPhoneAccountDetailsQuery } = useFixedPhoneSlice();
  const {
    data: fixedPhoneAccountDetailsData,
    isLoading: isFixedPhoneAccountDetailsLoading,
    // isFetching: isFixedPhoneAccountDetailsFetching,
  } = useGetFixedPhoneAccountDetailsQuery(void 0, {
    skip: !customerId || !hasFixedPhoneService || !shouldFetchAccountDetails,
  });

  const accountStatus = useMemo(() => {
    const isOnDashboard = location.pathname.includes("/my-account/dashboard");

    // If on dashboard, return status based on the currently active tab
    if (isOnDashboard) {
      if (dashboardActiveTab === "fixedPhone") {
        // Return fixed phone account status when fixed phone tab is active
        const fpSubs = fixedPhoneDashboardData?.subscriptions;
        if (fpSubs?.length) {
          const idx = clampFixedPhoneLineIndex(
            fixedPhoneDashboardSubTabIndex,
            fpSubs.length,
          );
          const fp = fpSubs[idx];
          if (fp?.accountStatus) {
            const status = fp.accountStatus.status;
            const isActive = fp.accountStatus.isActive;
            return status?.toLowerCase() === "active" || isActive;
          }
        }
        return null;
      }

      if (dashboardActiveTab === "digitalSecurity") {
        if (digitalSecurityDashboardData?.data?.status) {
          return isDigitalSecurityAccountActiveStatus(
            digitalSecurityDashboardData.data.status,
          );
        }
        return null;
      }

      if (dashboardActiveTab === "mobile") {
        // Return mobile dashboard status when mobile tab is active
        if (mobileDashboardData?.data?.status) {
          const status = mobileDashboardData.data.status;
          return status === "active" || status !== "accountSuspended";
        }
        // Fallback to digital security status if mobile data is missing
        if (digitalSecurityDashboardData?.data?.status) {
          return isDigitalSecurityAccountActiveStatus(
            digitalSecurityDashboardData.data.status,
          );
        }
        return null;
      }
    }

    // If on sim-service, return status based on the currently active tab
    if (isOnSimService) {
      if (simServiceActiveTab === "fixedPhone") {
        // Per-line status from account details (normalized `subscriptions[]`), matching SIM Service sub-tab.
        const detailSubs = fixedPhoneAccountDetailsData?.data?.subscriptions;
        if (detailSubs?.length) {
          const idx = clampFixedPhoneLineIndex(
            fixedPhoneSimServiceSubTabIndex,
            detailSubs.length,
          );
          const status = detailSubs[idx]?.account?.accountStatus;
          if (status) {
            return status.toLowerCase() === "active";
          }
        }
        return null;
      }

      if (simServiceActiveTab === "mobile") {
        // Return mobile dashboard status when mobile tab is active
        if (mobileDashboardData?.data?.status) {
          const status = mobileDashboardData.data.status;
          return status === "active" || status !== "accountSuspended";
        }
        // Fallback to digital security status if mobile data is missing
        if (digitalSecurityDashboardData?.data?.status) {
          return isDigitalSecurityAccountActiveStatus(
            digitalSecurityDashboardData.data.status,
          );
        }
        return null;
      }
    }

    // For all other My Account pages (payment history, manage profile, etc.)
    const dashSubs = fixedPhoneDashboardData?.subscriptions;
    if (dashSubs?.length) {
      const idx = clampFixedPhoneLineIndex(
        fixedPhoneDashboardSubTabIndex,
        dashSubs.length
      );
      const fp = dashSubs[idx];
      if (fp?.accountStatus) {
        const status = fp.accountStatus.status;
        const isActive = fp.accountStatus.isActive;
        return status?.toLowerCase() === "active" || isActive;
      }
    }

    const detSubsOther = fixedPhoneAccountDetailsData?.data?.subscriptions;
    if (detSubsOther?.length) {
      const idx = clampFixedPhoneLineIndex(
        fixedPhoneDashboardSubTabIndex,
        detSubsOther.length
      );
      const status = detSubsOther[idx]?.account?.accountStatus;
      if (status) {
        return status.toLowerCase() === "active";
      }
    }

    if (mobileDashboardData?.data?.status) {
      const status = mobileDashboardData.data.status;
      return status === "active" || status !== "accountSuspended";
    }

    if (digitalSecurityDashboardData?.data?.status) {
      return isDigitalSecurityAccountActiveStatus(
        digitalSecurityDashboardData.data.status,
      );
    }

    return null;
  }, [
    location.pathname,
    dashboardActiveTab,
    simServiceActiveTab,
    fixedPhoneDashboardData,
    fixedPhoneAccountDetailsData,
    mobileDashboardData,
    digitalSecurityDashboardData,
    isOnSimService,
    fixedPhoneDashboardSubTabIndex,
    fixedPhoneSimServiceSubTabIndex,
  ]);

  const isLoading = useMemo(() => {
    const isOnDashboard = location.pathname.includes("/my-account/dashboard");

    if (isOnDashboard) {
      if (dashboardActiveTab === "fixedPhone") {
        return isFixedPhoneDashboardLoading;
      }
      if (dashboardActiveTab === "digitalSecurity") {
        return isDigitalSecurityLoading;
      }
      if (dashboardActiveTab === "mobile") {
        return isMobileDashboardLoading;
      }
      // If no active tab, check if any data is loading
      return (
        isFixedPhoneDashboardLoading ||
        isMobileDashboardLoading ||
        isDigitalSecurityLoading
      );
    }

    if (isOnSimService) {
      if (simServiceActiveTab === "fixedPhone") {
        return isFixedPhoneAccountDetailsLoading;
      }
      if (simServiceActiveTab === "mobile") {
        return isMobileDashboardLoading || isDigitalSecurityLoading;
      }
      // If no active tab, check if any data is loading
      return (
        isFixedPhoneAccountDetailsLoading ||
        isFixedPhoneDashboardLoading ||
        isMobileDashboardLoading ||
        isDigitalSecurityLoading
      );
    }

    // For other pages, check if any relevant data is loading
    return (
      isFixedPhoneDashboardLoading ||
      isMobileDashboardLoading ||
      isDigitalSecurityLoading
    );
  }, [
    location.pathname,
    dashboardActiveTab,
    simServiceActiveTab,
    isFixedPhoneDashboardLoading,
    isFixedPhoneAccountDetailsLoading,
    isMobileDashboardLoading,
    isDigitalSecurityLoading,
    isOnSimService,
  ]);

  const getAccountStatus = useCallback(() => {
    if (!customerId) return;

    if (hasCellularMobile) {
      getDashboardData({ customerId });
    }
    if (hasDigitalSecurityPlan) {
      getDigitalSecurityDashboard({ customerId });
    }
    if (hasFixedPhoneService) {
      getFixedPhoneDashboard({ customerId });
    }
  }, [
    customerId,
    hasCellularMobile,
    hasDigitalSecurityPlan,
    hasFixedPhoneService,
    getDashboardData,
    getDigitalSecurityDashboard,
    getFixedPhoneDashboard,
  ]);

  return {
    isActive: accountStatus,
    isLoading,
    getAccountStatus,
  };
};
